/**
 * HenniPenny workbench home — the branded front door at the app root.
 *
 * The public landing (hennipenny.com) sells the lab; this home opens the tool. It
 * carries the same voice and palette as the landing — calm, witty, "the sky isn't
 * falling, it's a dataset" — and hands the visitor straight into the NILM workbench,
 * which runs entirely in the browser and needs no sign-in to try.
 */
import type { ReactNode } from 'react';
import { ArrowRight, Grid3x3, LineChart, LogIn, ShieldCheck } from 'lucide-react';
import { HenniPennyLockup, HenniPennyMark } from '@/brand/HenniPennyMark';
import { BRAND_MANIFESTO, CONTACT_EMAIL, LANDING_URL, WRITING_DESK_URL } from '@/brand/brand';

export function HomePage({
  onOpenWorkbench,
  onSignIn,
  onSignOut,
  authed,
  userEmail,
  themeToggle,
}: {
  onOpenWorkbench: () => void;
  onSignIn: () => void;
  onSignOut: () => void;
  authed: boolean;
  userEmail?: string | null;
  themeToggle: ReactNode;
}) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* NAV */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          maxWidth: 1180,
          width: '100%',
          margin: '0 auto',
          padding: '22px 28px',
          flexWrap: 'wrap',
        }}
      >
        <HenniPennyLockup size={40} wordSize={21} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <a
            href={WRITING_DESK_URL}
            className="mono"
            style={{
              border: '1px solid var(--theme-border-strong, var(--theme-border))',
              borderRadius: 8,
              color: 'var(--theme-muted)',
              padding: '6px 12px',
              fontSize: 13,
              textDecoration: 'none',
            }}
          >
            Writing desk →
          </a>
          {themeToggle}
          <a
            href={LANDING_URL}
            className="mono"
            style={{ fontSize: 13, color: 'var(--theme-muted)', letterSpacing: '0.02em' }}
          >
            hennipenny.com
          </a>
          {authed ? (
            <>
              {userEmail ? (
                <span
                  className="mono"
                  style={{
                    fontSize: 12,
                    color: 'var(--theme-muted)',
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userEmail}
                </span>
              ) : null}
              <button type="button" onClick={onSignOut} className="sp-btn" style={{ fontSize: 13 }}>
                Sign out
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onSignIn}
              className="sp-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13 }}
            >
              <LogIn size={14} />
              Sign in
            </button>
          )}
        </div>
      </nav>

      {/* HERO */}
      <header
        style={{
          position: 'relative',
          maxWidth: 1180,
          width: '100%',
          margin: '0 auto',
          padding: '40px 28px 8px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
          gap: 44,
          alignItems: 'center',
        }}
        className="hp-hero"
      >
        <div>
          <div
            className="mono"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12.5,
              color: 'var(--theme-warn)',
              border: '1px solid var(--theme-warn-line)',
              borderRadius: 999,
              padding: '6px 14px',
              marginBottom: 24,
              letterSpacing: '0.04em',
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--theme-accent)',
                display: 'inline-block',
              }}
            />
            sky.status = NOT_FALLING · confidence 0.997
          </div>
          <h1
            style={{
              fontFamily: 'var(--theme-font-display)',
              fontSize: 'clamp(38px, 5.4vw, 66px)',
              lineHeight: 1.03,
              margin: '0 0 20px',
              fontWeight: 800,
              letterSpacing: '-0.03em',
            }}
          >
            The sky isn't falling.
            <br />
            <span style={{ color: 'var(--theme-accent)' }}>It's a load profile.</span>
          </h1>
          <p
            style={{
              fontSize: 'clamp(16px, 1.5vw, 20px)',
              lineHeight: 1.55,
              color: 'var(--theme-muted)',
              maxWidth: '34em',
              margin: '0 0 30px',
            }}
          >
            The HenniPenny workbench turns appliance-level power predictions into auditable evidence —
            precision, recall, F1 and a confusion matrix scored against a pinned benchmark, deterministically,
            right in your browser. <strong style={{ color: 'var(--theme-text)' }}>{BRAND_MANIFESTO}</strong>
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              onClick={onOpenWorkbench}
              className="sp-btn sp-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 16, padding: '13px 26px', borderRadius: 999 }}
            >
              Open the workbench
              <ArrowRight size={17} />
            </button>
            {!authed ? (
              <button
                type="button"
                onClick={onSignIn}
                className="sp-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 16, padding: '13px 22px', borderRadius: 999 }}
              >
                <LogIn size={16} />
                Sign in
              </button>
            ) : null}
            <span className="mono" style={{ fontSize: 12, color: 'var(--theme-dim)' }}>
              // no acorns were harmed
            </span>
          </div>
        </div>

        <div className="hp-hero-art" style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              inset: '8% 6%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(242,199,126,0.22) 0%, rgba(242,199,126,0) 70%)',
            }}
          />
          <img
            src="/logo.png"
            alt="A bespectacled chicken calmly modeling the sky, surrounded by turbines, solar panels and a neural network"
            style={{
              width: 'min(100%, 400px)',
              height: 'auto',
              position: 'relative',
              filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.4))',
            }}
          />
        </div>
      </header>

      {/* WHAT THE WORKBENCH DOES */}
      <section style={{ maxWidth: 1180, width: '100%', margin: '0 auto', padding: '48px 28px 20px' }}>
        <div className="hp-info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 18 }}>
          <HomeCard
            icon={<LineChart size={18} />}
            step="01 / model"
            title="Disaggregate the meter"
            body="Join appliance-level predictions to a pinned benchmark on sample_id and score the disaggregation — one household meter becomes a picture of every appliance behind it."
          />
          <HomeCard
            icon={<Grid3x3 size={18} />}
            step="02 / predict"
            title="Read the error structure"
            body="Per-appliance precision, recall and F1, plus the full confusion matrix — so a strong headline number on a rare appliance is never over-read."
          />
          <HomeCard
            icon={<ShieldCheck size={18} />}
            step="03 / prove"
            title="Seal it into evidence"
            body="The browser preview mirrors the figures the free researchharness CLI certifies into a sealed evidence.v1 bundle — verdict floored to inconclusive until a reviewer signs off. Rigorous, reproducible, honest."
          />
        </div>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          marginTop: 'auto',
          borderTop: '1px solid var(--theme-border)',
          padding: '28px',
        }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
            fontSize: 14,
            color: 'var(--theme-muted)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <HenniPennyMark size={26} />
            <span style={{ fontWeight: 700, color: 'var(--theme-text)' }}>HenniPenny</span>
            <span>· part of the Evidence Foundry research network</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--theme-muted)' }}>
              {CONTACT_EMAIL}
            </a>
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--theme-dim)' }}>
              est. before the sky fell
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HomeCard({ icon, step, title, body }: { icon: ReactNode; step: string; title: string; body: string }) {
  return (
    <div
      style={{
        background: 'var(--theme-panel)',
        border: '1px solid var(--theme-border)',
        borderRadius: 16,
        padding: '26px 24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: 'var(--theme-accent)' }}>
        {icon}
        <span className="mono" style={{ fontSize: 12, color: 'var(--theme-accent)', letterSpacing: '0.04em' }}>
          {step}
        </span>
      </div>
      <h3
        style={{
          fontFamily: 'var(--theme-font-display)',
          fontSize: 21,
          fontWeight: 700,
          margin: '0 0 8px',
          letterSpacing: '-0.01em',
          color: 'var(--theme-text)',
        }}
      >
        {title}
      </h3>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: 'var(--theme-muted)' }}>{body}</p>
    </div>
  );
}
